import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { companyWithAdminValidationSchema } from '../../utils/validationSchemas';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../utils/api';

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Toronto', label: 'Eastern Time - Canada' },
  { value: 'America/Vancouver', label: 'Pacific Time - Canada' },
  { value: 'America/Halifax', label: 'Atlantic Time - Canada' },
  { value: 'America/Winnipeg', label: 'Central Time - Canada' },
  { value: 'America/Edmonton', label: 'Mountain Time - Canada' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Europe/Rome', label: 'Central European Time (CET)' },
  { value: 'Europe/Madrid', label: 'Central European Time (CET)' },
  { value: 'Europe/Amsterdam', label: 'Central European Time (CET)' },
  { value: 'Europe/Brussels', label: 'Central European Time (CET)' },
  { value: 'Europe/Zurich', label: 'Central European Time (CET)' },
  { value: 'Europe/Stockholm', label: 'Central European Time (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time (EET)' },
  { value: 'Europe/Athens', label: 'Eastern European Time (EET)' },
  { value: 'Europe/Moscow', label: 'Moscow Standard Time (MSK)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore Standard Time (SGT)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWT)' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time (ACT)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Standard Time (NZST)' },
];

const INDUSTRY_OPTIONS = [
  { value: 'construction', label: 'Construction' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'warehousing', label: 'Warehousing' },
  { value: 'other', label: 'Other' },
];

const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

interface AddCompanyFormProps {
  onSuccess?: () => void;
}

type CompanyFormData = yup.InferType<typeof companyWithAdminValidationSchema>;

const AddCompanyForm: React.FC<AddCompanyFormProps> = ({ onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(companyWithAdminValidationSchema),
    defaultValues: {
      name: '',
      timezone: 'America/Los_Angeles',
      legal_name: '',
      industry: '',
      company_size: '',
      description: '',
      email: '',
      password: '',
      password_confirm: '',
      first_name: '',
      last_name: '',
      is_superuser: false,
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: yup.InferType<typeof companyWithAdminValidationSchema>) => {
    setLoading(true);
    setSubmitError(null);

    try {
      const payload = {
        name: data.name.trim(),
        timezone: data.timezone,
        email: data.email.toLowerCase().trim(),
        password: data.password,
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        is_superuser: data.is_superuser,
        ...(data.legal_name && data.legal_name.trim() && { legal_name: data.legal_name.trim() }),
        ...(data.industry && data.industry.trim() && { industry: data.industry.trim() }),
        ...(data.company_size && data.company_size.trim() && { company_size: data.company_size.trim() }),
        ...(data.description && data.description.trim() && { description: data.description.trim() }),
      };

      const response = await api.post('/companies/create_with_admin/', payload);

      if (response.data) {
        showSuccess(
          response.data.message ||
            `Successfully ${response.data.company_created ? 'created' : 'updated'} company: ${response.data.company.name}`
        );
        reset();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error('Create company error:', error);
      let errorMessage = 'Failed to create company. Please try again.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.non_field_errors?.[0]) {
          errorMessage = error.response.data.non_field_errors[0];
        } else {
          const errorValues = Object.values(error.response.data);
          if (errorValues.length > 0 && Array.isArray(errorValues[0])) {
            errorMessage = errorValues[0][0] as string;
          }
        }
      }
      setSubmitError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Add New Company
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create a new company and provision an admin account for it.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Company Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Company Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Company Name *"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.timezone} required>
                  <InputLabel id="timezone-label">Timezone *</InputLabel>
                  <Select 
                    {...field} 
                    labelId="timezone-label"
                    label="Timezone *"
                  >
                    {TIMEZONE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.timezone && (
                    <FormHelperText>{errors.timezone.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="legal_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Legal Name"
                  fullWidth
                  error={!!errors.legal_name}
                  helperText={errors.legal_name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.industry}>
                  <InputLabel id="industry-label">Industry</InputLabel>
                  <Select 
                    {...field} 
                    labelId="industry-label"
                    label="Industry"
                    value={field.value || ''}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {INDUSTRY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.industry && (
                    <FormHelperText>{errors.industry.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="company_size"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.company_size}>
                  <InputLabel id="company-size-label">Company Size</InputLabel>
                  <Select 
                    {...field} 
                    labelId="company-size-label"
                    label="Company Size"
                    value={field.value || ''}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {COMPANY_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.company_size && (
                    <FormHelperText>{errors.company_size.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>

          {/* Admin User Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Admin User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email *"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name *"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name *"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password *"
                  type="password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="password_confirm"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirm Password *"
                  type="password"
                  fullWidth
                  error={!!errors.password_confirm}
                  helperText={errors.password_confirm?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="is_superuser"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  }
                  label="Create as Django superuser (has access to all companies)"
                />
              )}
            />
          </Grid>

          {/* Submit Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  reset();
                  setSubmitError(null);
                }}
                disabled={loading || isSubmitting}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || isSubmitting}
                startIcon={loading || isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {loading || isSubmitting ? 'Creating...' : 'Create Company'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default AddCompanyForm;
